import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import React, { useState, useEffect } from 'react';
import { auth } from '../../../shared/config/firebase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import preloadData from '../../../shared/utils/preloadService';

function SignupForm({ onBackToLogin }) {
  const [step, setStep] = useState(1); // 1 = email, 2 = full name, 3 = password
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isOtpView, setIsOtpView] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle email pre-filling from URL parameters (for team invitations)
  // Also check if user exists in localStorage (from login redirect) and show OTP view
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
      // If email is pre-filled, start at step 2
      if (emailParam) {
        setStep(2);
      }
    }
    
    // Check if user exists in localStorage but is not verified (from login redirect)
    const storedUser = localStorage.getItem('User');
    const accessToken = localStorage.getItem('accessToken');
    if (storedUser && accessToken) {
      try {
        const user = JSON.parse(storedUser);
        // If user is not verified and we have access token, show OTP view
        if (!user.is_verified && user.email) {
          setEmail(user.email);
          setIsOtpView(true);
          console.log('User found in localStorage but not verified, showing OTP view');
        }
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (step === 1) {
      setIsFormValid(email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    } else if (step === 2) {
      setIsFormValid(fullName.trim() !== '');
    } else if (step === 3) {
      setIsFormValid(password.trim() !== '' && password === confirmPassword && password.length >= 6);
    }
  }, [email, fullName, password, confirmPassword, step]);

  const handleEmailContinue = (e) => {
    e.preventDefault();
    if (email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStep(2);
    }
  };

  const handleNameContinue = (e) => {
    e.preventDefault();
    if (fullName.trim() !== '') {
      setStep(3);
    }
  };

  const handleBackToEmail = () => {
    setStep(1);
  };

  const handleBackToName = () => {
    setStep(2);
    setPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    // Set loading state to true when submission starts
    setIsLoading(true);
  
    try {
      const response = await axiosInstance.post('/auth/create', {
        formData: {
          name: fullName,
          email,
          password
        },
      });
      console.log(response);
      
      // Handle both new user creation and existing unverified user cases
      if (response.data.accessToken) {
        localStorage.setItem('accessToken', response.data.accessToken);
      }
      
      if (response.data.user) {
        // Store user data
        localStorage.setItem('User', JSON.stringify(response.data.user));
        
        // If user requires verification (new or existing unverified), show OTP view
        if (response.data.requiresVerification || !response.data.user.is_verified) {
          setIsOtpView(true);
          if (response.data.message) {
            // Show message but don't block - user can enter OTP
            console.log(response.data.message);
          }
        } else {
          // User is already verified (shouldn't happen in signup, but handle it)
          alert(response.data.message || 'Account created successfully');
        }
      } else {
        alert(response.data.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error during signup:', error.response?.data || error.message);
      if (error.response) {
        const errorData = error.response.data;
        const errorMessage = errorData?.message || 'An error occurred. Please try again.';
        
        // If user exists and is verified, suggest login
        if (error.response.status === 400 && errorData?.error === 'USER_EXISTS_VERIFIED') {
          alert('User already exists. Please login instead.');
          // Optionally redirect to login
          if (onBackToLogin) {
            onBackToLogin();
          }
        } else {
          alert(errorMessage);
        }
      } else {
        console.error('Error during signup:', error);
        alert('An error occurred. Please try again.');
      }
    } finally {
      // Ensure loading state is set to false whether the request succeeds or fails
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus to next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleOtpConfirm = async () => {
    const otpCode = otp.join('');
    try {
      const response = await axiosInstance.post('/auth/verifyOTP', {
        email,
        otp: otpCode
      });
      console.log(response);
      
      if (response.status===200) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('User', JSON.stringify(response.data.user));
        
        // Use the default team from server response or fall back to username
        const defaultTeam = response.data.defaultTeam;
        if (defaultTeam) {
          localStorage.setItem('selectedTeam', defaultTeam.name);
          localStorage.setItem('selectedTeamData', JSON.stringify(defaultTeam));
          console.log('Using user\'s default team from OTP verification:', defaultTeam.name);
          
          // Fetch and cache subscription data for the team
          try {
            const subscriptionResponse = await axiosInstance.get(`/api/billing/status/${defaultTeam._id}`);
            if (subscriptionResponse.data && subscriptionResponse.data.success && subscriptionResponse.data.data) {
              const subscriptionData = subscriptionResponse.data.data;
              const { plan, status, hasCQIntelligence, isActive, limits, accessiblePages } = subscriptionData;
              
              // Update team data with subscription info
              const updatedTeamData = {
                ...defaultTeam,
                subscription: {
                  plan: plan || 'free',
                  status: status || 'incomplete',
                  hasCQIntelligence: hasCQIntelligence || false
                }
              };
              localStorage.setItem('selectedTeamData', JSON.stringify(updatedTeamData));
              
              // Cache subscription status separately for faster access using centralized data
              const subscriptionCache = {
                isActive: isActive || false,
                plan: plan || 'free',
                status: status || 'incomplete',
                hasCQIntelligence: hasCQIntelligence || false,
                limits: limits || {},
                accessiblePages: accessiblePages || ['dashboard', 'settings'],
                timestamp: Date.now()
              };
              
              // Store in sessionStorage (persists for current session)
              sessionStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
              
              // Also store in localStorage (for session recovery)
              localStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
              
              console.log('Subscription data cached during signup:', subscriptionCache);
              
              // Dispatch event to notify subscription context of the update
              window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
                detail: { teamData: updatedTeamData }
              }));
            }
          } catch (subscriptionError) {
            console.error('Error fetching subscription data during signup:', subscriptionError);
            // Continue with signup even if subscription fetch fails
          }
        } else {
          // Fallback to old behavior if no default team
          const fallbackTeam = email.split('@')[0];
          localStorage.setItem('selectedTeam', fallbackTeam);
          console.log('No default team found in OTP verification, using fallback:', fallbackTeam);
        }
        
        // Preload data before navigating to dashboard
        await preloadData();
        
        // Force refresh to ensure subscription context is properly initialized
        // This prevents the "Upgrade Now" screen from showing for premium users
        console.log('Signup successful, refreshing page to ensure proper subscription context initialization');
        window.location.href = '/dashboard';
      } else {
        // If verification fails, reset to signup view
        setIsOtpView(false);
        alert('Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP Verification Error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred during OTP verification. Please try again.';
      
      // Don't reset to signup view on OTP error - let user retry OTP or resend
      // Only show error message
      alert(errorMessage);
      
      // Clear OTP input fields for retry
      setOtp(['', '', '', '', '', '']);
      
      // Focus on first OTP input
      setTimeout(() => {
        const firstOtpInput = document.getElementById('otp-0');
        if (firstOtpInput) {
          firstOtpInput.focus();
        }
      }, 100);
    }
  };

  const handleResendOtp = async () => {
    try {
      const userEmail = JSON.parse(localStorage.getItem('User'))?.email || email;
      
      const response = await axiosInstance.post('/auth/resend-otp', {
        email: userEmail
      });
      
      if (response.data.message) {
        alert('New OTP has been sent to your email');
        // Reset OTP input fields
        setOtp(['', '', '', '', '', '']);
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      alert('Error resending OTP. Please try again.');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const googleLogin = async () => {
    const provider = new GoogleAuthProvider();
    
    // Add custom parameters for better security perception
    provider.setCustomParameters({
      prompt: 'select_account',
      login_hint: '',
      // Use app.cryptique.io domain for auth callback
      state: encodeURIComponent(JSON.stringify({origin: window.location.origin}))
    });
    
    try {
      setIsLoading(true);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const response = await axiosInstance.post('/auth/google-login', {
          name: user.displayName,
          email: user.email,
          auth_provider_info: user.photoURL, // Google profile URL for auth provider info
      });
      const aa=user.email.split('@')[0];
      
      if (response.data.user) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('User', JSON.stringify(response.data.user));
        
        // Use the default team from server response or fall back to username
        const defaultTeam = response.data.defaultTeam;
        if (defaultTeam) {
          localStorage.setItem('selectedTeam', defaultTeam.name);
          localStorage.setItem('selectedTeamData', JSON.stringify(defaultTeam));
          console.log('Using user\'s default team from Google signup:', defaultTeam.name);
          
          // Fetch and cache subscription data for the team
          try {
            const subscriptionResponse = await axiosInstance.get(`/api/billing/status/${defaultTeam._id}`);
            if (subscriptionResponse.data && subscriptionResponse.data.success && subscriptionResponse.data.data) {
              const subscriptionData = subscriptionResponse.data.data;
              const { plan, status, hasCQIntelligence, isActive, limits, accessiblePages } = subscriptionData;
              
              // Update team data with subscription info
              const updatedTeamData = {
                ...defaultTeam,
                subscription: {
                  plan: plan || 'free',
                  status: status || 'incomplete',
                  hasCQIntelligence: hasCQIntelligence || false
                }
              };
              localStorage.setItem('selectedTeamData', JSON.stringify(updatedTeamData));
              
              // Cache subscription status separately for faster access using centralized data
              const subscriptionCache = {
                isActive: isActive || false,
                plan: plan || 'free',
                status: status || 'incomplete',
                hasCQIntelligence: hasCQIntelligence || false,
                limits: limits || {},
                accessiblePages: accessiblePages || ['dashboard', 'settings'],
                timestamp: Date.now()
              };
              localStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
              
              console.log('Subscription data cached during Google signup:', subscriptionCache);
              
              // Dispatch event to notify subscription context of the update
              window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
                detail: { teamData: updatedTeamData }
              }));
            }
          } catch (subscriptionError) {
            console.error('Error fetching subscription data during Google signup:', subscriptionError);
            // Continue with signup even if subscription fetch fails
          }
        } else {
          // Fallback to old behavior if no default team
          localStorage.setItem('selectedTeam', aa);
          console.log('No default team found in Google signup, using fallback:', aa);
        }
        
        // Preload data before navigating to dashboard
        await preloadData();
        
        // Force refresh to ensure subscription context is properly initialized
        // This prevents the "Upgrade Now" screen from showing for premium users
        console.log('Google signup successful, refreshing page to ensure proper subscription context initialization');
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Error during Google login:', error);
      alert('Google login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP View
  if (isOtpView) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h2 
            className="text-2xl mb-2 text-[#0f172a]" 
            style={{ fontFamily: 'Raleway, sans-serif', fontWeight: '500' }}
          >
            Enter OTP
          </h2>
          <p className="text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>This should take no longer than 1 min</p>
        </div>

        <div className="flex justify-center gap-2 mb-4">
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              className="w-12 h-12 text-center border border-[#DDE2EE] rounded-lg text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            />
          ))}
        </div>

        <div className="text-center mb-4">
          <p className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
            If you haven't received your OTP yet,{' '}
            <button 
              onClick={handleResendOtp}
              className="text-[#8979FF] hover:text-[#7c6ae8] underline"
            >
              click here to send it again
            </button>
          </p>
        </div>

        <button 
          onClick={handleOtpConfirm}
          className="w-full py-3 rounded-lg font-medium transition-colors bg-[#8979FF] text-white hover:bg-[#7c6ae8]"
          style={{ fontFamily: 'Raleway, sans-serif' }}
        >
          Confirm
        </button>
      </div>
    );
  }

  // Step 1: Email entry
  if (step === 1) {
    return (
      <div className="space-y-6">
        <form onSubmit={handleEmailContinue} className="space-y-4">
          <div className="relative">
            <input 
              type="email" 
              id="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
              placeholder="Email address (Required)"
              required
              style={{ fontFamily: 'Raleway, sans-serif' }}
            />
          </div>

          <button 
            type="submit" 
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              isFormValid 
                ? 'bg-[#8979FF] text-white hover:bg-[#7c6ae8]' 
                : 'bg-[#DDE2EE] text-[#475569] cursor-not-allowed'
            }`}
            disabled={!isFormValid}
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            Continue
          </button>
        </form>

        <div className="text-center text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          <p>
            Already have an account?{' '}
            <button 
              onClick={onBackToLogin} 
              className="text-[#8979FF] hover:text-[#7c6ae8] font-medium inline-flex items-center gap-1"
            >
              Sign in
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="inline">
                <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Full Name entry
  if (step === 2) {
    return (
      <div className="space-y-6">
        <form onSubmit={handleNameContinue} className="space-y-4">
          {/* Full Name field */}
          <div className="relative">
            <input 
              type="text" 
              id="fullName" 
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
              placeholder="Full name (Required)"
              required
              autoFocus
              style={{ fontFamily: 'Raleway, sans-serif' }}
            />
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between gap-3">
            <button 
              type="button" 
              onClick={handleBackToEmail}
              className="text-sm text-[#475569] hover:text-[#8979FF] transition-colors"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              ← Use a different email
            </button>
          </div>

          {/* Continue button */}
          <button 
            type="submit" 
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              isFormValid 
                ? 'bg-[#8979FF] text-white hover:bg-[#7c6ae8]' 
                : 'bg-[#DDE2EE] text-[#475569] cursor-not-allowed'
            }`}
            disabled={!isFormValid}
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            Continue
          </button>
        </form>
      </div>
    );
  }

  // Step 3: Password entry
  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Password field */}
        <div>
          <div className="relative">
            <input 
              type={showPassword ? "text" : "password"} 
              id="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF] pr-12"
              style={{ fontFamily: 'Raleway, sans-serif' }}
              placeholder="Password (Required)"
              required
              autoFocus
            />
            <button 
              type="button" 
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-[#475569] hover:text-[#8979FF]"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {/* Confirm Password field - appears dynamically after password is entered */}
        {password && (
          <div>
            <div className="relative">
              <input 
                type={showConfirmPassword ? "text" : "password"} 
                id="confirmPassword" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF] pr-12"
                style={{ fontFamily: 'Raleway, sans-serif' }}
                placeholder="Confirm password (Required)"
                required
              />
              <button 
                type="button" 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-[#475569] hover:text-[#8979FF]"
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-between gap-3">
          <button 
            type="button" 
            onClick={handleBackToName}
            className="text-sm text-[#475569] hover:text-[#8979FF] transition-colors"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            ← Use a different name
          </button>
        </div>

        {/* Create Account button */}
        <button 
          type="submit" 
          className={`w-full py-3 rounded-lg font-medium transition-colors ${
            isFormValid 
              ? 'bg-[#8979FF] text-white hover:bg-[#7c6ae8]' 
              : 'bg-[#DDE2EE] text-[#475569] cursor-not-allowed'
          }`}
          disabled={!isFormValid || isLoading}
          style={{ fontFamily: 'Raleway, sans-serif' }}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating Account...
            </span>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center">
        <div className="flex-grow h-px bg-[#DDE2EE]"></div>
        <div className="px-4 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>OR</div>
        <div className="flex-grow h-px bg-[#DDE2EE]"></div>
      </div>

      {/* Google Sign in */}
      <button 
        onClick={googleLogin} 
        className="w-full py-3 px-4 bg-white hover:bg-gray-50 border border-[#DDE2EE] rounded-lg flex items-center justify-center gap-3 transition-colors text-[#0f172a] shadow-sm"
        style={{ fontFamily: 'Raleway, sans-serif' }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20">
          <path d="M19.8 10.2c0-.7-.1-1.4-.2-2h-9.6v3.8h5.5c-.2 1.2-1 2.3-2.1 3v2.5h3.4c2-1.8 3-4.5 3-7.3z" fill="#4285F4"/>
          <path d="M10 20c2.9 0 5.3-1 7-2.6l-3.4-2.6c-.9.6-2.1 1-3.6 1-2.8 0-5.1-1.9-6-4.4H.5v2.7c1.8 3.5 5.4 5.9 9.5 5.9z" fill="#34A853"/>
          <path d="M4 10.2c-.2-.6-.4-1.3-.4-2s.1-1.4.4-2V3.5H.5c-.8 1.6-1.3 3.4-1.3 5.2 0 1.8.5 3.6 1.3 5.2l3.5-2.7z" fill="#FBBC05"/>
          <path d="M10 3.9c1.6 0 3 .5 4.1 1.6l3-3C15.3.9 12.9 0 10 0 5.9 0 2.3 2.4.5 5.9l3.5 2.7c.8-2.5 3.2-4.7 6-4.7z" fill="#EA4335"/>
        </svg>
        <span>Sign up with Google</span>
      </button>

      <div className="text-center text-xs text-[#475569] mt-4" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <p>
          By signing up, you agree to our{' '}
          <a href="https://www.cryptique.io/legal/terms" target="_blank" rel="noopener noreferrer" className="text-[#8979FF] hover:text-[#7c6ae8] underline">
            Terms of Service
          </a>
          {' '}and{' '}
          <a href="https://www.cryptique.io/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#8979FF] hover:text-[#7c6ae8] underline">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}

export default SignupForm;
